import { pointInPolygon } from "./mapMeasurements";
import { createRainFieldInterpolator } from "./rainHeatmapUtils";

const COLOR_STOPS = [
  { t: 0, r: 33, g: 150, b: 243 },
  { t: 0.2, r: 0, g: 188, b: 212 },
  { t: 0.4, r: 76, g: 175, b: 80 },
  { t: 0.65, r: 255, g: 235, b: 59 },
  { t: 0.82, r: 255, g: 152, b: 0 },
  { t: 1, r: 244, g: 67, b: 54 },
];

const MAX_CANVAS_EDGE = 520;

function interpolateRainColor(mm, maxMm, minMm = 0) {
  const range = Math.max(Number(maxMm) - Number(minMm) || 0, 1);
  const ratio = Math.max(0, Math.min(1, (Number(mm) - Number(minMm || 0)) / range));
  let lower = COLOR_STOPS[0];
  let upper = COLOR_STOPS[COLOR_STOPS.length - 1];

  for (let index = 0; index < COLOR_STOPS.length - 1; index += 1) {
    if (ratio >= COLOR_STOPS[index].t && ratio <= COLOR_STOPS[index + 1].t) {
      lower = COLOR_STOPS[index];
      upper = COLOR_STOPS[index + 1];
      break;
    }
  }

  const stopSpan = upper.t - lower.t || 1;
  const localT = (ratio - lower.t) / stopSpan;

  return {
    r: Math.round(lower.r + (upper.r - lower.r) * localT),
    g: Math.round(lower.g + (upper.g - lower.g) * localT),
    b: Math.round(lower.b + (upper.b - lower.b) * localT),
  };
}

export function rainMmToRgba(mm, maxMm, alpha) {
  const color = interpolateRainColor(mm, maxMm);
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
}

export function rainMmToHex(mm, maxMm) {
  const color = interpolateRainColor(mm, maxMm);
  const toHex = (value) => {
    const hex = value.toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  };
  return `#${toHex(color.r)}${toHex(color.g)}${toHex(color.b)}`;
}

function unionRingBounds(rings) {
  let minLat = 90;
  let maxLat = -90;
  let minLng = 180;
  let maxLng = -180;
  let found = false;

  (rings || []).forEach((ring) => {
    (ring || []).forEach((point) => {
      if (!point) return;
      const lat = Number(point.lat);
      const lng = Number(point.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      found = true;
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
    });
  });

  if (!found) return null;
  const padLat = (maxLat - minLat) * 0.01 || 0.0002;
  const padLng = (maxLng - minLng) * 0.01 || 0.0002;
  return {
    minLat: minLat - padLat,
    maxLat: maxLat + padLat,
    minLng: minLng - padLng,
    maxLng: maxLng + padLng,
  };
}

function ringAabb(ring) {
  let minLat = 90;
  let maxLat = -90;
  let minLng = 180;
  let maxLng = -180;
  (ring || []).forEach((point) => {
    minLat = Math.min(minLat, Number(point.lat));
    maxLat = Math.max(maxLat, Number(point.lat));
    minLng = Math.min(minLng, Number(point.lng));
    maxLng = Math.max(maxLng, Number(point.lng));
  });
  return { minLat, maxLat, minLng, maxLng };
}

function pointInAabb(point, box) {
  return (
    point.lat >= box.minLat &&
    point.lat <= box.maxLat &&
    point.lng >= box.minLng &&
    point.lng <= box.maxLng
  );
}

function pointInAnyRing(point, rings, aabbs) {
  for (let index = 0; index < rings.length; index += 1) {
    if (aabbs[index] && !pointInAabb(point, aabbs[index])) continue;
    if (pointInPolygon(point, rings[index])) return true;
  }
  return false;
}

function paintRainIdw(canvas, rings, gauges, maxMm, minMm = 0) {
  const context = canvas.getContext("2d");
  if (!context) return false;

  const bounds = unionRingBounds(rings);
  const hasRain = (gauges || []).some((gauge) => Number(gauge.mm) > 0);
  if (!bounds || !hasRain) {
    canvas.width = 1;
    canvas.height = 1;
    context.clearRect(0, 0, 1, 1);
    return false;
  }

  const geoWidth = Math.max(bounds.maxLng - bounds.minLng, 0.00001);
  const geoHeight = Math.max(bounds.maxLat - bounds.minLat, 0.00001);
  const aspect = geoWidth / geoHeight;
  let cols;
  let rows;
  if (aspect >= 1) {
    cols = MAX_CANVAS_EDGE;
    rows = Math.max(2, Math.round(MAX_CANVAS_EDGE / aspect));
  } else {
    rows = MAX_CANVAS_EDGE;
    cols = Math.max(2, Math.round(MAX_CANVAS_EDGE * aspect));
  }

  canvas.width = cols;
  canvas.height = rows;

  const image = context.createImageData(cols, rows);
  const data = image.data;
  const aabbs = rings.map(ringAabb);
  const interpolateMm = createRainFieldInterpolator(gauges);

  for (let row = 0; row < rows; row += 1) {
    const lat = bounds.maxLat - ((row + 0.5) / rows) * geoHeight;
    for (let col = 0; col < cols; col += 1) {
      const lng = bounds.minLng + ((col + 0.5) / cols) * geoWidth;
      const sample = { lat, lng };
      const offset = (row * cols + col) * 4;
      if (!pointInAnyRing(sample, rings, aabbs)) {
        data[offset + 3] = 0;
        continue;
      }
      const mm = interpolateMm(sample);
      if (mm <= 0) {
        data[offset + 3] = 0;
        continue;
      }
      const color = interpolateRainColor(mm, maxMm, minMm);
      const intensity = Math.max(
        0,
        Math.min(1, (mm - Number(minMm || 0)) / Math.max(Number(maxMm) - Number(minMm || 0), 1))
      );
      data[offset] = color.r;
      data[offset + 1] = color.g;
      data[offset + 2] = color.b;
      data[offset + 3] = Math.round((0.58 + intensity * 0.3) * 255);
    }
  }

  context.putImageData(image, 0, 0);
  return true;
}

function overlayPixelBox(projection, maps, bounds) {
  const sw = projection.fromLatLngToDivPixel(
    new maps.LatLng(bounds.minLat, bounds.minLng)
  );
  const ne = projection.fromLatLngToDivPixel(
    new maps.LatLng(bounds.maxLat, bounds.maxLng)
  );
  if (!sw || !ne) return null;
  const left = Math.min(sw.x, ne.x);
  const top = Math.min(sw.y, ne.y);
  return {
    left,
    top,
    width: Math.max(1, Math.abs(ne.x - sw.x)),
    height: Math.max(1, Math.abs(sw.y - ne.y)),
  };
}

function overlayDataKey(rings, gauges, maxMm, minMm) {
  const rain = (gauges || [])
    .map((gauge) => `${Number(gauge.lat).toFixed(5)}:${Number(gauge.lng).toFixed(5)}:${Number(gauge.mm) || 0}`)
    .join("|");
  return `${(rings || []).length}:${minMm}:${maxMm}:${rain}`;
}

export function createRainIdwOverlay(maps) {
  function RainIdwOverlay() {
    this.div_ = null;
    this.canvas_ = null;
    this.rings_ = [];
    this.gauges_ = [];
    this.minMm_ = 0;
    this.maxMm_ = 1;
    this.paintedKey_ = "";
    this.paintedZoom_ = null;
  }

  RainIdwOverlay.prototype = new maps.OverlayView();

  RainIdwOverlay.prototype.onAdd = function onAdd() {
    const div = document.createElement("div");
    div.style.position = "absolute";
    div.style.pointerEvents = "none";
    div.style.zIndex = "1";
    const canvas = document.createElement("canvas");
    canvas.style.display = "block";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.imageRendering = "auto";
    div.appendChild(canvas);
    this.div_ = div;
    this.canvas_ = canvas;
    const panes = this.getPanes();
    if (panes && panes.overlayLayer) {
      panes.overlayLayer.appendChild(div);
    }
  };

  RainIdwOverlay.prototype.draw = function draw() {
    if (!this.div_ || !this.canvas_) return;
    const projection = this.getProjection();
    if (!projection) return;

    const bounds = unionRingBounds(this.rings_);
    if (!bounds) {
      this.div_.style.width = "0px";
      this.div_.style.height = "0px";
      return;
    }

    const map = this.getMap();
    const zoom = map && map.getZoom ? map.getZoom() : null;
    const dataKey = overlayDataKey(
      this.rings_,
      this.gauges_,
      this.maxMm_,
      this.minMm_
    );
    if (this.paintedKey_ !== dataKey || this.paintedZoom_ !== zoom) {
      paintRainIdw(
        this.canvas_,
        this.rings_,
        this.gauges_,
        this.maxMm_,
        this.minMm_
      );
      this.paintedKey_ = dataKey;
      this.paintedZoom_ = zoom;
    }

    const box = overlayPixelBox(projection, maps, bounds);
    if (!box) return;
    this.div_.style.left = `${box.left}px`;
    this.div_.style.top = `${box.top}px`;
    this.div_.style.width = `${box.width}px`;
    this.div_.style.height = `${box.height}px`;
  };

  RainIdwOverlay.prototype.onRemove = function onRemove() {
    if (this.div_ && this.div_.parentNode) {
      this.div_.parentNode.removeChild(this.div_);
    }
    this.div_ = null;
    this.canvas_ = null;
    this.paintedKey_ = "";
    this.paintedZoom_ = null;
  };

  RainIdwOverlay.prototype.setData = function setData(data) {
    this.rings_ = (data && data.rings) || [];
    this.gauges_ = (data && data.gauges) || [];
    this.minMm_ = Number(data && data.minMm) || 0;
    this.maxMm_ = (data && data.maxMm) || 1;
    this.paintedKey_ = "";
    if (this.getProjection()) {
      this.draw();
    }
  };

  return new RainIdwOverlay();
}

export function clearRainIdwOverlay(overlay) {
  if (!overlay) return;
  overlay.setMap(null);
}
